import { useState, useEffect, useRef } from 'react'
import { Check, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useAvailablePriceLists } from './useAvailablePriceLists'

interface PriceListEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPriceList: string | null
  onSave: (priceList: string) => Promise<void>
  isSaving: boolean
}

export function PriceListEditorModal({
  open,
  onOpenChange,
  currentPriceList,
  onSave,
  isSaving,
}: PriceListEditorModalProps) {
  const [selectedValue, setSelectedValue] = useState<string | null>(
    currentPriceList,
  )
  const [saveError, setSaveError] = useState<string | null>(null)
  const prevOpen = useRef(false)

  const { priceLists, isLoading, isError } = useAvailablePriceLists(open)

  // Reset state when modal opens
  useEffect(() => {
    if (open && !prevOpen.current) {
      setSelectedValue(currentPriceList)
      setSaveError(null)
    }
    prevOpen.current = open
  }, [open, currentPriceList])

  const hasChanged = selectedValue !== currentPriceList
  const canSave = hasChanged && selectedValue && !isSaving
  const isEmpty = !isLoading && !isError && priceLists.length === 0

  const handleSave = async () => {
    if (!selectedValue) return
    setSaveError(null)
    try {
      await onSave(selectedValue)
    } catch {
      setSaveError('Failed to save. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader className="pl-12">
          <DialogTitle>Price List</DialogTitle>
          <DialogDescription>
            This is the price list customers see in your store.
          </DialogDescription>
        </DialogHeader>

        {saveError && (
          <div className="mx-6 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm">
            <p className="text-sm text-red-600 dark:text-red-400">
              {saveError}
            </p>
          </div>
        )}

        <Command className="border-0 shadow-none">
          <CommandInput placeholder="Search price lists..." />
          <CommandList className="max-h-[240px]">
            {isLoading ? (
              <div className="p-4 space-y-3">
                <Skeleton className="w-full h-8 rounded-md" />
                <Skeleton className="w-3/4 h-8 rounded-md" />
                <Skeleton className="w-5/6 h-8 rounded-md" />
              </div>
            ) : isError ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Failed to load price lists. Please close and try again.
              </div>
            ) : (
              <>
                <CommandEmpty>
                  No selling price lists found. Contact your Zaviago admin.
                </CommandEmpty>
                <CommandGroup>
                  {priceLists.map((pl) => (
                    <CommandItem
                      key={pl.name}
                      value={pl.name}
                      onSelect={() => setSelectedValue(pl.name)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          selectedValue === pl.name
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      {pl.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>

        <DialogFooter>
          {!isEmpty && (
            <>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave}
                className="min-w-[80px]"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
